// Tareas de un puesto del estudio: un agente de IA (conversaciones que atendió
// y en qué quedaron) o un asesor (sus casos abiertos). Sin texto de mensajes.
import React, { useEffect, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { agentLights } from '../../hooks/useSupervision';
import { timeAgo } from '../chat/format';
import { CONV_STATUS, PRESENCE, SESSION_STATE, intentText, phoneOf } from '../supervision/labels';

const RELOAD_MS = 4000;

export default function StationSheet({ station, data, theme, live, now, feed, onClose }) {
  const c = theme.colors;
  return (
    <aside style={{ ...st.sheet, borderColor: c.cardBorder }}>
      <div style={st.head}>
        <button style={st.back} onClick={onClose} aria-label="Cerrar tareas">←</button>
        {station.kind === 'ai'
          ? <AgentTasks id={station.id} data={data} theme={theme} live={live} now={now} feed={feed} />
          : <HumanTasks id={station.id} data={data} />}
      </div>
    </aside>
  );
}

function AgentTasks({ id, data, theme, live, now, feed }) {
  const { apiFetch } = useApi();
  const [res, setRes] = useState(null);
  const [error, setError] = useState('');
  const agent = data.ai_agents.find(a => a.id === id);
  const lights = agentLights(live, id, now);
  const doing = live.doing[id];
  // Se recarga cuando ese agente termina un turno (como máximo cada 4 s)
  const lastOwn = feed.find(e => e.actor_id === id && e.type !== 'ai_agent.turn_started')?.created_at;

  useEffect(() => {
    let alive = true;
    const t = setTimeout(() => {
      apiFetch(`/api/admin/supervision/agents/${encodeURIComponent(id)}/tasks`)
        .then(r => { if (alive) { setRes({ ...r, tasks: Array.isArray(r?.tasks) ? r.tasks : [] }); setError(''); } })
        .catch(e => alive && setError(e.message));
    }, res ? RELOAD_MS : 0);
    return () => { alive = false; clearTimeout(t); };
    // res solo decide si esperar; no debe disparar otra carga
  }, [apiFetch, id, lastOwn]); // eslint-disable-line

  if (!agent) return <div style={st.muted}>Este agente ya no está en el estudio.</div>;
  return (
    <div style={st.body}>
      <div>
        <div style={st.title}>{agent.name}</div>
        <div style={st.muted}>{agent.role}</div>
      </div>
      <div style={{ ...st.now, borderColor: lights.busy ? theme.colors.onAir : 'var(--border)' }}>
        {lights.busy ? <strong style={{ color: theme.colors.onAir }}>● {theme.words.onAir}</strong> : <strong>En espera</strong>}
        <div>
          {doing
            ? `${lights.busy ? 'Ahora' : 'Lo último'}: ${intentText(doing.intent)}${doing.session_id ? ` · ${phoneOf(doing.session_id)}` : ''}`
            : 'Sin actividad reciente'}
        </div>
      </div>
      <div style={st.stats}>
        <Stat n={agent.tasks_30m} label="tareas en 30 min" />
        <Stat n={agent.turns_15m} label="turnos en 15 min" />
        <Stat n={agent.errors_1h} label="errores en 1 h" warn={agent.errors_1h > 0} />
      </div>
      <h4 style={st.h4}>Conversaciones que atendió (24 h)</h4>
      {error && <div style={st.error}>⚠️ {error}</div>}
      {!res && !error && <div style={st.muted}>Cargando…</div>}
      {res && res.tasks.length === 0 && <div style={st.muted}>Sin conversaciones en las últimas 24 horas.</div>}
      <div style={st.list}>
        {res?.tasks.map(t => (
          <div key={t.session_id} style={st.row}>
            <div style={{ display: 'flex', gap: 8 }}>
              <strong style={{ flex: 1 }}>{t.phone || phoneOf(t.session_id)}</strong>
              <span style={st.time}>{timeAgo(t.last_at)}</span>
            </div>
            <div style={st.muted}>
              {intentText(t.last_intent)} · {t.turns} {t.turns === 1 ? 'turno' : 'turnos'}
              {t.escalated ? ' · escaló a humano' : ''}
            </div>
            <div style={st.statusLine}>
              {t.status === 'assigned' && t.assigned_name ? `👤 con ${t.assigned_name}` : `${CONV_STATUS[t.status] || 'sin estado'}`}
              {t.escalation_reason && (t.status === 'queued' || t.status === 'assigned') ? ` · ${t.escalation_reason}` : ''}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function HumanTasks({ id, data }) {
  const h = data.humans.find(x => x.id === id);
  if (!h) return <div style={st.muted}>Este asesor ya no está activo.</div>;
  const p = PRESENCE[h.presence] || PRESENCE.offline;
  const mine = data.sessions.rows.filter(r => r.assigned_user_id === id && (r.state === 'attending' || r.state === 'waiting_client'));
  return (
    <div style={st.body}>
      <div>
        <div style={st.title}>{h.name}</div>
        <div style={st.muted}>{p.icon} {p.label}{h.role === 'admin' ? ' · admin' : ''} · {h.agent_type_label}{!h.receives_assignments ? ' · no recibe casos' : ''}</div>
      </div>
      <div style={st.stats}>
        <Stat n={h.open_cases} label="casos abiertos" />
        <Stat n={mine.filter(r => r.state === 'attending').length} label="por responder" warn={mine.some(r => r.state === 'attending')} />
        <Stat n={mine.filter(r => r.state === 'waiting_client').length} label="esperando cliente" />
      </div>
      <h4 style={st.h4}>Sus casos</h4>
      {mine.length === 0 && <div style={st.muted}>Sin casos abiertos.</div>}
      <div style={st.list}>
        {mine.map(r => (
          <div key={r.session_id} style={st.row}>
            <div style={{ display: 'flex', gap: 8 }}>
              <strong style={{ flex: 1 }}>{r.phone || phoneOf(r.session_id)}</strong>
              <span style={st.time}>asignado {timeAgo(r.assigned_at)}</span>
            </div>
            <div style={st.muted}>
              {SESSION_STATE[r.state].icon} {SESSION_STATE[r.state].label}
              {r.escalation_reason ? ` · ${r.escalation_reason}` : ''}
              {r.last_message_at ? ` · último mensaje ${timeAgo(r.last_message_at)}` : ''}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Stat({ n, label, warn }) {
  return (
    <div style={st.stat}>
      <div style={{ ...st.statN, color: warn ? 'var(--warning)' : 'var(--text)' }}>{n}</div>
      <div style={st.statL}>{label}</div>
    </div>
  );
}

const st = {
  sheet: { background: 'var(--surface)', border: '1px solid', borderRadius: 14, padding: 14 },
  head: { display: 'flex', gap: 10, alignItems: 'flex-start' },
  back: { background: 'var(--surface-2)', border: '1px solid var(--border-strong)', borderRadius: 8, color: 'var(--text)', width: 30, height: 30, cursor: 'pointer', flexShrink: 0 },
  body: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 10 },
  title: { fontSize: 15, fontWeight: 800, color: 'var(--text)' },
  muted: { fontSize: 12, color: 'var(--text-3)' },
  now: { border: '1px solid', borderRadius: 10, padding: '8px 10px', fontSize: 12.5, color: 'var(--text)', display: 'flex', flexDirection: 'column', gap: 2 },
  stats: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 },
  stat: { background: 'var(--surface-2)', borderRadius: 10, padding: '8px 6px', textAlign: 'center' },
  statN: { fontSize: 20, fontWeight: 800, fontVariantNumeric: 'tabular-nums' },
  statL: { fontSize: 10.5, color: 'var(--text-2)' },
  h4: { fontSize: 11, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: 0.8, margin: '4px 0 0' },
  list: { display: 'flex', flexDirection: 'column', gap: 6, maxHeight: '42vh', overflowY: 'auto' },
  row: { border: '1px solid var(--border)', borderRadius: 8, padding: '7px 10px', fontSize: 12.5, color: 'var(--text)', background: 'var(--surface-2)', display: 'flex', flexDirection: 'column', gap: 2 },
  statusLine: { fontSize: 12, color: 'var(--accent-text)' },
  time: { fontSize: 11, color: 'var(--text-3)', whiteSpace: 'nowrap' },
  error: { fontSize: 12, color: 'var(--danger)' },
};
